import { Provider } from "@redotech/redo-order-common/provider";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { MarketingEntryPoint } from "./advanced-flow/schemas/marketing/marketing";

/**
 * Structured discount offer type used when calling getCodeForDiscount.
 * This is converted to a string internally for database storage.
 */
export const discountOfferSchema = z.object({
  value: z.number(),
  type: z.enum(["percentage"]),
});
export type DiscountOffer = z.infer<typeof discountOfferSchema>;

export enum TrackingPageDiscountSource {
  GAME = "game",
  UPSELL = "upsell",
}

export enum GeneratedDiscountCodeSourceEnum {
  MARKETING = "marketing",
  TRACKING_PAGE = "tracking_page",
  SHOPPER_AI = "shopper_ai",
  SHOPPER_AGENT = "shopper_agent",
  PERSONALIZED_SLIP = "personalized_slip",
}

export const generatedDiscountCodeSource = [
  GeneratedDiscountCodeSourceEnum.MARKETING,
  GeneratedDiscountCodeSourceEnum.TRACKING_PAGE,
  GeneratedDiscountCodeSourceEnum.SHOPPER_AI,
  GeneratedDiscountCodeSourceEnum.SHOPPER_AGENT,
  GeneratedDiscountCodeSourceEnum.PERSONALIZED_SLIP,
] as const;
export type GeneratedDiscountCodeSource =
  (typeof generatedDiscountCodeSource)[number];

export const SHOPPER_AGENT_DISCOUNT_SCOPE = "shopper_agent" as const;

export const generatedDiscountCodePurpose = [
  "campaign",
  "automation",
  "signup_form",
  "signup_flow",
  "tracking_page_upsell",
  "game",
  "marketing_conversation",
  "shopper_conversation",
  "marketing_agent",
  "personalized_slip",
  "preview",
  "unknown",
] as const;
export type GeneratedDiscountCodePurpose =
  (typeof generatedDiscountCodePurpose)[number];

const baseGeneratedDiscountCodeKeysSchema = z.object({
  discountId: zExt.objectId(),
  customerId: zExt.objectId(),
});

const baseSignupFlowGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    entryPoint: createEnum(
      Object.values(MarketingEntryPoint).filter(
        (ep) => ep !== MarketingEntryPoint.SIGNUP_FORM,
      ) as [MarketingEntryPoint, ...MarketingEntryPoint[]],
    ),
    automationId: zExt.objectId().optional(),
    automationExecutionId: zExt.objectId().optional(),
  });

const previewGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({ isPreview: z.literal(true) });
type PreviewGeneratedDiscountCodeKeys = z.infer<
  typeof previewGeneratedDiscountCodeKeysSchema
>;

const nonFormSignupFlowGeneratedDiscountCodeKeysSchema =
  baseSignupFlowGeneratedDiscountCodeKeysSchema.extend({
    entryPoint: createEnum(
      Object.values(MarketingEntryPoint).filter(
        (ep) => ep !== MarketingEntryPoint.SIGNUP_FORM,
      ) as [MarketingEntryPoint, ...MarketingEntryPoint[]],
    ),
  });
type NonFormSignupFlowGeneratedDiscountCodeKeys = z.infer<
  typeof nonFormSignupFlowGeneratedDiscountCodeKeysSchema
>;

const formSignupFlowGeneratedDiscountCodeKeysSchema =
  baseSignupFlowGeneratedDiscountCodeKeysSchema.extend({
    entryPoint: z.literal(MarketingEntryPoint.SIGNUP_FORM),
    formId: zExt.objectId(),
  });
type FormSignupFlowGeneratedDiscountCodeKeys = z.infer<
  typeof formSignupFlowGeneratedDiscountCodeKeysSchema
>;

const campaignGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({ campaignId: zExt.objectId() });
type CampaignGeneratedDiscountCodeKeys = z.infer<
  typeof campaignGeneratedDiscountCodeKeysSchema
>;

const automationGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    automationId: zExt.objectId(),
    automationExecutionId: zExt.objectId(),
  });
type AutomationGeneratedDiscountCodeKeys = z.infer<
  typeof automationGeneratedDiscountCodeKeysSchema
>;

const gameVictoryGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    orderId: z.string(),
    gameType: z.string(),
    isPreview: z.literal(true).optional(),
  });
type GameVictoryGeneratedDiscountCodeKeys = z.infer<
  typeof gameVictoryGeneratedDiscountCodeKeysSchema
>;

const trackingPageUpsellGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    orderId: z.string(),
    trackingPageUpsell: z.string(),
  });
type TrackingPageUpsellGeneratedDiscountCodeKeys = z.infer<
  typeof trackingPageUpsellGeneratedDiscountCodeKeysSchema
>;

const marketingConversationGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    marketingConversationId: zExt.objectId(),
  });
type MarketingConversationGeneratedDiscountCodeKeys = z.infer<
  typeof marketingConversationGeneratedDiscountCodeKeysSchema
>;

const shopperConversationGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    shopperAiConversationId: zExt.objectId(),
  });
type ShopperConversationGeneratedDiscountCodeKeys = z.infer<
  typeof shopperConversationGeneratedDiscountCodeKeysSchema
>;

const shopperAgentGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    shopperAgentScope: z.literal(SHOPPER_AGENT_DISCOUNT_SCOPE),
    discountOffer: z.string().optional(), // e.g., "10%", "20%"
  });
type ShopperAgentGeneratedDiscountCodeKeys = z.infer<
  typeof shopperAgentGeneratedDiscountCodeKeysSchema
>;

const marketingAgentGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    discountOffer: z.string(), // e.g., "10%", "20%", "$40"
  });
type MarketingAgentGeneratedDiscountCodeKeys = z.infer<
  typeof marketingAgentGeneratedDiscountCodeKeysSchema
>;

// A personalized-insert discount code. customerId is required: a slip code is
// minted only when the order's customer is resolved. Generation logs and skips
// the code when there is no customer (expected to be rare), so we can track how
// often that happens and revisit if needed.
const personalizedSlipGeneratedDiscountCodeKeysSchema = z.object({
  discountId: zExt.objectId(),
  customerId: zExt.objectId(),
  personalizedSlipTemplateId: zExt.objectId(),
});
type PersonalizedSlipGeneratedDiscountCodeKeys = z.infer<
  typeof personalizedSlipGeneratedDiscountCodeKeysSchema
>;

const generatedDiscountCodeKeysSchema = z.union([
  nonFormSignupFlowGeneratedDiscountCodeKeysSchema,
  formSignupFlowGeneratedDiscountCodeKeysSchema,
  campaignGeneratedDiscountCodeKeysSchema,
  automationGeneratedDiscountCodeKeysSchema,
  previewGeneratedDiscountCodeKeysSchema,
  gameVictoryGeneratedDiscountCodeKeysSchema,
  trackingPageUpsellGeneratedDiscountCodeKeysSchema,
  marketingConversationGeneratedDiscountCodeKeysSchema,
  shopperConversationGeneratedDiscountCodeKeysSchema,
  shopperAgentGeneratedDiscountCodeKeysSchema,
  marketingAgentGeneratedDiscountCodeKeysSchema,
  personalizedSlipGeneratedDiscountCodeKeysSchema,
]);

export const generatedDiscountCodeSchema = z.object({
  _id: zExt.objectId(),
  discountCode: z.string(),
  teamId: zExt.objectId(),
  createdAt: z.date(),
  updatedAt: z.date(),
  keys: generatedDiscountCodeKeysSchema,
  source: z
    .enum(generatedDiscountCodeSource)
    .default(GeneratedDiscountCodeSourceEnum.MARKETING),
  expiresAt: z.date().optional(),
  externalId: z.string().optional(),
  externalSource: z.enum(Provider).optional(),
  usedAt: z.date().optional(),
  usedOnOrderId: z.string().optional(),
});
export type GeneratedDiscountCode = z.infer<typeof generatedDiscountCodeSchema>;
export type GeneratedDiscountCodeKeys = z.infer<
  typeof generatedDiscountCodeKeysSchema
>;
const partialGeneratedDiscountCodeKeysSchema = z.object({
  discountId: zExt.objectId(),
  customerId: zExt.objectId().optional(),
  formId: zExt.objectId().optional(),
  automationId: zExt.objectId().optional(),
  automationExecutionId: zExt.objectId().optional(),
  campaignId: zExt.objectId().optional(),
  entryPoint: createEnum(MarketingEntryPoint).optional(),
  isPreview: z.literal(true).optional(),
  orderId: z.string().optional(),
  gameType: z.string().optional(),
  trackingPageUpsell: z.string().optional(),
  marketingConversationId: zExt.objectId().optional(),
  shopperAiConversationId: zExt.objectId().optional(),
  shopperAgentScope: z.literal(SHOPPER_AGENT_DISCOUNT_SCOPE).optional(),
  discountOffer: z.string().optional(),
  personalizedSlipTemplateId: zExt.objectId().optional(),
});
export type PartialGeneratedDiscountCodeKeys = z.infer<
  typeof partialGeneratedDiscountCodeKeysSchema
>;

export function isNonFormSignupFlowGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is NonFormSignupFlowGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.customerId &&
    keys.entryPoint &&
    keys.entryPoint !== MarketingEntryPoint.SIGNUP_FORM
  );
}

export function isFormSignupFlowGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is FormSignupFlowGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.customerId &&
    keys.entryPoint === MarketingEntryPoint.SIGNUP_FORM &&
    keys.formId
  );
}

export function isCampaignGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is CampaignGeneratedDiscountCodeKeys {
  return !!(keys.discountId && keys.customerId && keys.campaignId);
}

export function isAutomationGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is AutomationGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.customerId &&
    keys.automationId &&
    keys.automationExecutionId
  );
}

export function isPreviewGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is PreviewGeneratedDiscountCodeKeys {
  return !!(keys.discountId && keys.customerId && keys.isPreview);
}

export function isGameVictoryGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is GameVictoryGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.orderId &&
    keys.gameType &&
    keys.customerId
  );
}

export function isTrackingPageUpsellGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is TrackingPageUpsellGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.orderId &&
    keys.trackingPageUpsell &&
    keys.customerId
  );
}

export function isMarketingConversationGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is MarketingConversationGeneratedDiscountCodeKeys {
  return !!(keys.discountId && keys.customerId && keys.marketingConversationId);
}

export function isShopperConversationGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is ShopperConversationGeneratedDiscountCodeKeys {
  return !!(keys.discountId && keys.customerId && keys.shopperAiConversationId);
}

export function isShopperAgentGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is ShopperAgentGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.customerId &&
    keys.shopperAgentScope === SHOPPER_AGENT_DISCOUNT_SCOPE
  );
}

export function isMarketingAgentGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is MarketingAgentGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.customerId &&
    keys.discountOffer &&
    !keys.shopperAgentScope
  );
}

export function isPersonalizedSlipGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is PersonalizedSlipGeneratedDiscountCodeKeys {
  return !!(keys.discountId && keys.personalizedSlipTemplateId);
}

export function isGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys | undefined,
): keys is GeneratedDiscountCodeKeys {
  if (!keys) {
    return false;
  }
  return (
    isNonFormSignupFlowGeneratedDiscountCodeKeys(keys) ||
    isFormSignupFlowGeneratedDiscountCodeKeys(keys) ||
    isAutomationGeneratedDiscountCodeKeys(keys) ||
    isCampaignGeneratedDiscountCodeKeys(keys) ||
    isGameVictoryGeneratedDiscountCodeKeys(keys) ||
    isTrackingPageUpsellGeneratedDiscountCodeKeys(keys) ||
    isPreviewGeneratedDiscountCodeKeys(keys) ||
    isMarketingConversationGeneratedDiscountCodeKeys(keys) ||
    isShopperConversationGeneratedDiscountCodeKeys(keys) ||
    isShopperAgentGeneratedDiscountCodeKeys(keys) ||
    isMarketingAgentGeneratedDiscountCodeKeys(keys) ||
    isPersonalizedSlipGeneratedDiscountCodeKeys(keys)
  );
}

export function getGeneratedDiscountCodePurpose(
  keys: PartialGeneratedDiscountCodeKeys | undefined,
): GeneratedDiscountCodePurpose {
  if (!keys) {
    return "unknown";
  }
  if (isPreviewGeneratedDiscountCodeKeys(keys)) {
    return "preview";
  }
  if (isFormSignupFlowGeneratedDiscountCodeKeys(keys)) {
    return "signup_form";
  }
  if (isNonFormSignupFlowGeneratedDiscountCodeKeys(keys)) {
    return "signup_flow";
  }
  if (isMarketingConversationGeneratedDiscountCodeKeys(keys)) {
    return "marketing_conversation";
  }
  if (isPersonalizedSlipGeneratedDiscountCodeKeys(keys)) {
    return "personalized_slip";
  }
  if (isAutomationGeneratedDiscountCodeKeys(keys)) {
    return "automation";
  }
  if (isCampaignGeneratedDiscountCodeKeys(keys)) {
    return "campaign";
  }
  if (isGameVictoryGeneratedDiscountCodeKeys(keys)) {
    return "game";
  }
  if (isTrackingPageUpsellGeneratedDiscountCodeKeys(keys)) {
    return "tracking_page_upsell";
  }
  if (isShopperConversationGeneratedDiscountCodeKeys(keys)) {
    return "shopper_conversation";
  }
  if (isShopperAgentGeneratedDiscountCodeKeys(keys)) {
    return "shopper_conversation";
  }
  if (isMarketingAgentGeneratedDiscountCodeKeys(keys)) {
    return "marketing_agent";
  }
  return "unknown";
}
